import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject, type OnDestroy } from '@angular/core';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import { BehaviorSubject, distinctUntilChanged, tap } from 'rxjs';
import { SubSink } from 'subsink';
import type { InventoryOwner } from '../../../shared/models/api/inventory-owner.model';
import { InventoryOwnerService } from '../../../shared/services/api/inventory-owner.service';

@Component({
  selector: 'opa-inventory-owner-delete',
  imports: [CommonModule, PorscheDesignSystemModule],
  templateUrl: './inventory-owner-delete.component.html',
  styleUrl: './inventory-owner-delete.component.scss',
})
export class InventoryOwnerDeleteComponent implements OnDestroy {
  private readonly inventoryOwnerService = inject(InventoryOwnerService);
  private readonly toastManager = inject(ToastManager);

  @Input({ required: true }) open = false;
  @Input({ required: true }) inventoryOwner: InventoryOwner | undefined = undefined;
  @Output() dismiss = new EventEmitter<InventoryOwner | undefined>();

  private readonly subSink = new SubSink();
  private readonly submitting = new BehaviorSubject(false);
  readonly submitting$ = this.submitting.pipe(distinctUntilChanged());

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  close(inventoryOwner?: InventoryOwner): void {
    this.dismiss.emit(inventoryOwner);
  }

  submit(): void {
    const inventoryOwner = this.inventoryOwner;
    if (!inventoryOwner) return;

    this.submitting.next(true);
    this.subSink.sink = this.inventoryOwnerService
      .deleteInventoryOwner(inventoryOwner.id)
      .pipe(
        tap({
          next: () => {
            this.toastManager.addMessage({
              text: `Owner ${inventoryOwner.name} deleted`,
              state: 'success',
            });
            this.submitting.next(false);
            this.dismiss.emit(inventoryOwner);
          },
          error: () => this.submitting.next(false),
        }),
      )
      .subscribe();
  }
}
